import {
  Box,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import React, { FC } from 'react';
import { CustomInputEvent, TransactionPayment } from 'src/types';
import { TransactionDetailsARCell } from './TransactionARTableCell';
import { TransferFeeTableCell } from './TransferFeeTableCell';
import { formatCurrency, getPaymentTypeColor } from 'src/utils';
import DeleteIcon from '@material-ui/icons/Delete';
import { colors } from 'src/constants';
import SyncIcon from '@material-ui/icons/Sync';
import { slices, useAppDispatch } from 'src/redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { usePermissions, useSnackBar } from 'src/hooks';
import {
  hasExistingVoucherPayment,
  onTapTapCustomerPaymentAddedViaInternal
} from 'src/redux/slices/taptap-customer';

interface Props {
  payment: TransactionPayment;
  index: number;
  isDeleted?: boolean;
  canDeleteTransactionPayment: boolean;
  transactionNo?: string;
  onSaveARField: (
    e: any,
    payment: TransactionPayment,
    field: 'transfer_fee' | 'acknowledgement_receipt_no'
  ) => void;
  onDeletePayment: (payment: TransactionPayment) => void;
  onChangeARFieldText: (e: CustomInputEvent, index: number) => void;
  onChangeTFFieldText: (e: CustomInputEvent, index: number) => void;
  totalBalance?: number;
}

const { actions: transactionPaymentActions } = slices.transactionPayments;

export const PaymentRow: FC<Props> = ({
  payment,
  index,
  isDeleted = false,
  canDeleteTransactionPayment,
  transactionNo,
  onSaveARField,
  onChangeARFieldText,
  onChangeTFFieldText,
  onDeletePayment
}) => {
  const dispatch = useAppDispatch();
  const snackBar = useSnackBar();
  const { canSyncTransactionPaymentVoucher } = usePermissions();

  const isDisabledDeletePayment = payment?.payment_type === 'VOUCHER';

  const syncVoucher = async (payment_id: number, amount?: number) => {
    if (!payment_id) {
      return;
    }
    const payload = {
      payment_id: payment_id
    };
    const response = unwrapResult(
      await dispatch(transactionPaymentActions.syncVoucherPaymentThunk(payload))
    );

    if (response.success) {
      snackBar.show({
        message: 'Voucher synced successfully',
        severity: 'success'
      });

      if (!(await hasExistingVoucherPayment(transactionNo || ''))) {
        await onTapTapCustomerPaymentAddedViaInternal({
          transaction_no: transactionNo,
          category: 'payment',
          payment_type: 'VOUCHER',
          amount: amount,
          acknowledgement_receipt_no: ''
        });
      }

      window.location.reload();
    } else {
      snackBar.show({
        message: response.message || 'Failed to sync voucher',
        severity: 'error'
      });
    }
  };

  return (
    <TableRow
      hover
      key={payment.id}
      style={isDeleted ? { backgroundColor: colors.error } : {}}
    >
      <TableCell>{payment?.date_posted || '--'}</TableCell>
      <TransactionDetailsARCell
        data={payment}
        index={index}
        isDeleted={isDeleted}
        onSave={onSaveARField}
        onChangeText={onChangeARFieldText}
      />
      <TableCell>
        <Box display="flex" alignItems="center">
          <Typography
            variant="h6"
            style={{
              color: getPaymentTypeColor(payment?.payment_type)
            }}
          >
            {payment?.payment_type || '--'}
          </Typography>
          {payment?.payment_type === 'VOUCHER' &&
            canSyncTransactionPaymentVoucher && (
              <IconButton
                size="small"
                disabled={isDeleted}
                onClick={() =>
                  payment.id !== undefined &&
                  syncVoucher(payment.id, payment.amount)
                }
                style={{ marginLeft: 8 }}
              >
                <SyncIcon style={{ color: colors.primary }} />
              </IconButton>
            )}
        </Box>
      </TableCell>
      <TableCell>{formatCurrency(payment?.amount)}</TableCell>
      <TransferFeeTableCell
        data={payment}
        index={index}
        onSave={onSaveARField}
        onChangeText={onChangeTFFieldText}
      />
      <TableCell>{payment?.created_by || '--'}</TableCell>
      {canDeleteTransactionPayment && (
        <>
          <Tooltip
            title={
              isDisabledDeletePayment
                ? 'Please remove voucher first, this will automatically void after removing voucher'
                : ''
            }
          >
            <TableCell>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePayment(payment);
                }}
                disabled={isDisabledDeletePayment}
              >
                <DeleteIcon
                  color={isDisabledDeletePayment ? 'disabled' : 'secondary'}
                />
              </IconButton>
            </TableCell>
          </Tooltip>
        </>
      )}
    </TableRow>
  );
};
